import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, MoreHorizontal, Tag as TagIcon, X } from 'lucide-react';
import { MarkdownPrimitive } from '@almanac/catalog';
import { useApi } from '../api/useApi';
import { useMe } from '../api/MeContext';
import type { ResearchListItem } from '../api/client';
import { formatTimestampDate } from '../lib/format';
import {
  isResearchNew,
  loadResearchSeen,
  markResearchSeen,
  type ResearchSeenState,
} from '../lib/research-seen';

/**
 * Who queued the item. A specific person is encoded as `user:<sub>` so the
 * whole control is one string and drops straight into a `<select>` value.
 */
type OwnerFilter = 'mine' | 'everyone' | `user:${string}`;

function ownerKey(scope: string): string {
  return `almanac.research.v1.${scope}.owner`;
}

function isOwnerFilter(value: string): value is OwnerFilter {
  return value === 'mine' || value === 'everyone' || value.startsWith('user:');
}

/** The viewer's last owner choice on this browser, or 'mine' by default. */
function loadOwnerFilter(scope: string): OwnerFilter {
  let raw: string | null;
  try {
    raw = localStorage.getItem(ownerKey(scope));
  } catch {
    return 'mine';
  }
  if (raw && isOwnerFilter(raw)) return raw;
  return 'mine';
}

function saveOwnerFilter(scope: string, value: OwnerFilter): void {
  try {
    localStorage.setItem(ownerKey(scope), value);
  } catch {
    // Filter memory is best-effort.
  }
}

/** Fallback label for an owner with no `app_user` row: a short, stable id stub. */
function shortenSub(userId: string): string {
  if (userId.length <= 10) return userId;
  return `${userId.slice(0, 8)}…`;
}

/** The header count line: item total once loaded, a placeholder before that. */
function itemCountLabel(loaded: boolean, count: number): string {
  if (!loaded) return 'Loading…';
  if (count === 1) return '1 item';
  return `${count} items`;
}

/** Close `onDismiss` on Escape or a pointer press outside `ref`, while `open`. */
function useDismiss(
  ref: React.RefObject<HTMLElement | null>,
  open: boolean,
  onDismiss: () => void,
) {
  // Callers pass a fresh closure every render, so hold the latest in a ref and
  // key the listeners on `open` alone — otherwise every keystroke in the tag
  // search detaches and re-attaches two document listeners.
  const latest = useRef(onDismiss);
  useEffect(() => {
    latest.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && ref.current?.contains(target)) return;
      latest.current();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') latest.current();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, open]);
}

const CONTROL_CLASS =
  'inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-sm text-foreground hover:border-primary/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

interface TagFilterProps {
  tags: string[];
  selected: ReadonlySet<string>;
  onToggle: (tag: string) => void;
  onClear: () => void;
}

/**
 * The whole tag facet behind one button: a searchable checkbox list in a
 * popover, replacing the wall of chips that used to run five rows deep.
 */
function TagFilter({ tags, selected, onToggle, onClear }: TagFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useDismiss(containerRef, open, () => setOpen(false));

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return tags;
    return tags.filter((tag) => tag.toLowerCase().includes(needle));
  }, [tags, query]);

  // Anchored to the filter row on phones (`sm:relative` leaves the nearest
  // positioned ancestor as the row) so a 288px popover cannot push a 375px
  // viewport into horizontal scroll; button-anchored once there is room.
  return (
    <div className="sm:relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="true"
        className={CONTROL_CLASS}
      >
        <TagIcon className="size-3.5 text-muted-foreground" aria-hidden />
        Tags
        {selected.size > 0 ? (
          <span className="rounded-full bg-primary/15 px-1.5 text-[11px] font-medium text-primary">
            {selected.size}
          </span>
        ) : null}
        <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-20 mt-1.5 w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-xl">
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter tags"
            placeholder="Filter tags…"
            className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
          />
          <div className="mt-1.5 max-h-56 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-1 py-2 text-sm text-muted-foreground">No tags match.</p>
            ) : null}
            {visible.map((tag) => (
              <label
                key={tag}
                className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={selected.has(tag)}
                  onChange={() => onToggle(tag)}
                  className="size-3.5 accent-primary"
                />
                <span className="truncate">{tag}</span>
              </label>
            ))}
          </div>
          {selected.size > 0 ? (
            <button
              type="button"
              onClick={onClear}
              className="mt-1 w-full rounded-md border-t border-border px-1 py-1.5 text-left text-sm text-muted-foreground hover:text-foreground"
            >
              Clear tags
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Low-traffic switches (archived is a handful of rows) tucked out of the way,
 * so the filter row leads with what people actually reach for.
 */
function MoreMenu({
  showArchived,
  onShowArchivedChange,
}: {
  showArchived: boolean;
  onShowArchivedChange: (value: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useDismiss(containerRef, open, () => setOpen(false));

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="More filters"
        className={CONTROL_CLASS}
      >
        <MoreHorizontal className="size-4 text-muted-foreground" aria-hidden />
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-20 mt-1.5 w-[min(14rem,calc(100vw-2rem))] rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-xl">
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-muted">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => onShowArchivedChange(e.target.checked)}
              className="size-3.5 accent-primary"
            />
            Show archived
          </label>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The shared research library index: server-side lexical search over the full
 * artifact, narrowed by who queued it, its tags, and whether the viewer has
 * read it yet.
 */
export function ResearchPage() {
  const api = useApi();
  const me = useMe();
  const sub = me?.sub ?? null;
  const [items, setItems] = useState<ResearchListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<ReadonlySet<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('mine');
  const [seenState, setSeenState] = useState<ResearchSeenState | null>(null);

  // The viewer's identity arrives a tick after mount; both their stored filter
  // choice and their read-state baseline are keyed by it.
  useEffect(() => {
    if (!sub) {
      setSeenState(null);
      return;
    }
    setOwnerFilter(loadOwnerFilter(sub));
    setSeenState(loadResearchSeen(sub));
  }, [sub]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .listResearch({
        includeArchived: showArchived,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      })
      .then((r) => {
        if (!cancelled) setItems(r.research);
        return undefined;
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [api, debouncedSearch, showArchived]);

  function changeOwnerFilter(value: OwnerFilter) {
    setOwnerFilter(value);
    if (sub) saveOwnerFilter(sub, value);
  }

  function markSeen(item: ResearchListItem) {
    const next = markResearchSeen(sub, item);
    if (next) setSeenState(next);
  }

  const allTags = useMemo(() => {
    if (!items) return [];
    return [...new Set([...selectedTags, ...items.flatMap((item) => item.tags)])].sort();
  }, [items, selectedTags]);

  // Labels seen on any load, so a remembered person keeps their name even when
  // the current results no longer contain them.
  const ownerLabels = useRef(new Map<string, string>());
  useEffect(() => {
    for (const item of items ?? []) {
      const label = item.owner_display_name ?? shortenSub(item.owner_user_id);
      ownerLabels.current.set(item.owner_user_id, label);
    }
  }, [items]);

  /** Distinct owners in the loaded list, labeled and sorted for the picker. */
  const owners = useMemo(() => {
    const byId = new Map<string, string>();
    for (const item of items ?? []) {
      const label = item.owner_display_name ?? shortenSub(item.owner_user_id);
      if (!byId.has(item.owner_user_id)) byId.set(item.owner_user_id, label);
    }
    return [...byId]
      .map(([userId, label]) => ({ userId, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  // A `<select>` whose value matches no option silently renders the first one,
  // so a remembered person absent from this load would look like "Mine" while
  // filtering to someone else. Keep them in the list until the viewer changes it.
  const ownerOptions = useMemo(() => {
    if (!ownerFilter.startsWith('user:')) return owners;
    const userId = ownerFilter.slice('user:'.length);
    if (owners.some((owner) => owner.userId === userId)) return owners;
    const label = ownerLabels.current.get(userId) ?? shortenSub(userId);
    return [...owners, { userId, label }].sort((a, b) => a.label.localeCompare(b.label));
  }, [owners, ownerFilter]);

  /** Display name behind the current `user:` selection, for explanatory copy. */
  const selectedOwnerLabel =
    ownerOptions.find((owner) => `user:${owner.userId}` === ownerFilter)?.label ?? 'That person';

  // "Mine" is the default, but a teammate who has never queued research would
  // land on an empty page. Fall back to everyone — only for an unfiltered load,
  // so a search that misses their own items does not silently widen the view.
  const mineIsEmpty =
    ownerFilter === 'mine' &&
    sub !== null &&
    !debouncedSearch &&
    items !== null &&
    items.length > 0 &&
    !items.some((item) => item.owner_user_id === sub);

  // A remembered person can disappear from the list (everything archived, or a
  // search that excludes them); don't strand the viewer on a blank page.
  const missingPerson =
    ownerFilter.startsWith('user:') &&
    !debouncedSearch &&
    items !== null &&
    !owners.some((owner) => `user:${owner.userId}` === ownerFilter);

  const effectiveOwner: OwnerFilter = (() => {
    if (!sub) return 'everyone';
    if (mineIsEmpty || missingPerson) return 'everyone';
    return ownerFilter;
  })();

  function toggleTag(tag: string) {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function matchesOwner(item: ResearchListItem): boolean {
    if (effectiveOwner === 'everyone') return true;
    if (effectiveOwner === 'mine') return item.owner_user_id === sub;
    return item.owner_user_id === effectiveOwner.slice('user:'.length);
  }

  function isNew(item: ResearchListItem): boolean {
    if (!seenState) return false;
    return isResearchNew(seenState, item);
  }

  const scoped = (items ?? []).filter((item) => {
    if (!matchesOwner(item)) return false;
    if (selectedTags.size > 0 && !item.tags.some((tag) => selectedTags.has(tag))) return false;
    return true;
  });
  const newCount = scoped.filter((item) => isNew(item)).length;
  const filtered = newOnly ? scoped.filter((item) => isNew(item)) : scoped;

  return (
    <div>
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-8 sm:px-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Research
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {itemCountLabel(items !== null, filtered.length)}
          </p>
        </div>

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        {items ? (
          <div className="space-y-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search research"
              placeholder="Search title, summary, article, poster…"
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
            />
            <div className="relative flex flex-wrap items-center gap-2">
              <select
                value={ownerFilter}
                onChange={(e) => {
                  const { value } = e.target;
                  if (isOwnerFilter(value)) changeOwnerFilter(value);
                }}
                aria-label="Filter by who queued the research"
                className={`${CONTROL_CLASS} pr-1.5`}
              >
                <option value="mine">Mine</option>
                <option value="everyone">Everyone</option>
                {ownerOptions.map((owner) => (
                  <option key={owner.userId} value={`user:${owner.userId}`}>
                    {owner.label}
                  </option>
                ))}
              </select>

              <TagFilter
                tags={allTags}
                selected={selectedTags}
                onToggle={toggleTag}
                onClear={() => setSelectedTags(new Set())}
              />

              <button
                type="button"
                onClick={() => setNewOnly((value) => !value)}
                aria-pressed={newOnly}
                className={
                  newOnly
                    ? `${CONTROL_CLASS} border-primary/50 bg-primary/10 text-primary`
                    : CONTROL_CLASS
                }
              >
                New only
                {newCount > 0 ? (
                  <span className="rounded-full bg-primary/15 px-1.5 text-[11px] font-medium text-primary">
                    {newCount}
                  </span>
                ) : null}
              </button>

              <div className="ml-auto">
                <MoreMenu showArchived={showArchived} onShowArchivedChange={setShowArchived} />
              </div>
            </div>

            {selectedTags.size > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {[...selectedTags].sort().map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    aria-label={`Remove tag ${tag}`}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/50 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary"
                  >
                    {tag}
                    <X className="size-3" aria-hidden />
                  </button>
                ))}
              </div>
            ) : null}

            {mineIsEmpty ? (
              <p className="text-xs text-muted-foreground">
                You have no research in this view — showing everyone’s.
              </p>
            ) : null}

            {missingPerson ? (
              <p className="text-xs text-muted-foreground">
                {selectedOwnerLabel} has nothing here — showing everyone’s.
              </p>
            ) : null}
          </div>
        ) : null}

        {!items && !error ? (
          <div className="space-y-3" aria-hidden>
            {[0, 1, 2].map((n) => (
              <div key={n} className="h-28 animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : null}

        {items && filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No research matches.</p>
        ) : null}

        {filtered.map((item) => (
          <article
            key={item.research_id}
            className="space-y-2 rounded-xl border border-border bg-card p-4"
          >
            <div className="flex flex-wrap items-baseline gap-2">
              <Link
                to={`/research/${item.research_id}`}
                onClick={() => markSeen(item)}
                className="font-display text-lg font-semibold text-foreground hover:underline"
              >
                {item.title}
              </Link>
              {isNew(item) ? (
                <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                  New
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{formatTimestampDate(item.published_at ?? item.created_at)}</span>
              {item.owner_display_name ? <span>{item.owner_display_name}</span> : null}
              {item.kind === 'finding' ? (
                <span className="rounded-full border border-accent/30 bg-accent/20 px-2 py-0.5 text-[11px] text-accent-foreground">
                  finding
                </span>
              ) : null}
              {item.has_poster ? (
                <span className="rounded-full border border-accent/30 bg-accent/20 px-2 py-0.5 text-[11px] text-accent-foreground">
                  poster
                </span>
              ) : null}
              {item.visibility !== 'internal' ? (
                <span className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {item.visibility}
                </span>
              ) : null}
              {item.status === 'archived' ? (
                <span className="rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[11px] text-muted-foreground">
                  archived
                </span>
              ) : null}
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
            <MarkdownPrimitive body={item.summary_md} />
          </article>
        ))}
      </main>
    </div>
  );
}
